/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {zodResolver} from '@hookform/resolvers/zod'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'

import {
    Alert,
    AlertIcon,
    AspectRatio,
    Box,
    Flex,
    Heading,
    Image,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'

import {useNewsletterSubscribeMutation} from '../../clients/ssr/hooks/use-newsletter-subscribe'
import {colors, localStorageKeys, styleConfigChakraKeys} from '../../constants'
import {getTitleCase} from '../../hooks/use-title-case'
import {inputNameFromCmsName} from '../../utils'
import {newsletterSchema} from '../../validations'
import {LegalLink} from '../legal-link'
import Link from '../link'

const NewsLetterModal = ({data: newsLetterData, isOpen, onClose}) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.newsletterModal)
    const form = useForm({resolver: zodResolver(newsletterSchema)})
    const intl = useIntl()
    const [formSubmitSuccess, setFormSubmitSuccess] = useState(false)
    const newsletterMutation = useNewsletterSubscribeMutation()

    const emailInput = newsLetterData?.content?.fieldsCollection?.items?.find(
        (item) => item?.name?.includes('email')
    )

    const sendNewsletterSubscriptionRequest = async (payload) => {
        newsletterMutation.mutate(payload, {
            onSuccess: () => {
                localStorage.setItem(
                    localStorageKeys.newsLetterSubscription,
                    'true'
                )
                setFormSubmitSuccess(true)
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'newsletter',
                    newsletterEmail: payload.email
                })
            },
            onError: (error) => {
                form.setError('global', {
                    type: 'manual',
                    message: error?.message
                })
            }
        })
    }
    const {formatMessage} = useIntl()

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open')
        } else {
            document.body.classList.remove('modal-open')
        }
    }, [isOpen])

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay zIndex={1500} />
            <ModalContent
                {...styles.modalContent}
                borderRadius="6px"
                overflow="hidden"
                containerProps={{
                    zIndex: 1501
                }}
                width="100%"
                maxWidth={{base: '556px', md: '724px'}}
            >
                <ModalCloseButton
                    color={{base: 'white', md: 'black'}}
                    _focusVisible={{boxShadow: 'none'}}
                    fontSize="12px"
                    zIndex="1"
                />
                <ModalBody
                    as="form"
                    onSubmit={form.handleSubmit(
                        sendNewsletterSubscriptionRequest
                    )}
                    {...styles.modalBody}
                    p="0"
                    minHeight={{base: '430px', md: '100%'}}
                    backgroundColor={colors.platinum}
                    display="flex"
                >
                    <Box width={{base: '100%', lg: '47%'}}>
                        <AspectRatio
                            ratio={2 / 3}
                            height={{base: '40vh', md: '100%'}}
                        >
                            <Image
                                src={newsLetterData?.media?.desktop?.url}
                                alt="newsletter banner image"
                                objectFit="cover"
                            />
                        </AspectRatio>
                    </Box>
                    {formSubmitSuccess ? (
                        <Flex
                            flexDirection="column"
                            p={{base: '40px 20px', md: '96px 56px'}}
                            gap={{base: '14px', md: '29px'}}
                            justifyContent="center"
                            width={{base: '100%', lg: '53%'}}
                        >
                            <Heading
                                variant="h3"
                                fontFamily="Roboto"
                                fontWeight="300"
                            >
                                {newsLetterData?.content?.successMessage}
                            </Heading>
                            {/* TODO: Add title case for text */}
                            <Link
                                type={
                                    newsLetterData?.content?.afterSuccessLink
                                        ?.type
                                }
                                buttonStyles={styles.submitButton}
                                onLinkClick={onClose}
                            >
                                {getTitleCase(
                                    newsLetterData?.content?.afterSuccessLink
                                        .text,
                                    intl.locale
                                )}
                            </Link>
                        </Flex>
                    ) : (
                        <Flex
                            flexDirection="column"
                            p={{
                                base: '40px 20px 20px 20px',
                                lg: '96px 56px 30px 56px'
                            }}
                            gap={{base: '14px', lg: '29px'}}
                            justifyContent="center"
                            width={{base: '100%', lg: '53%'}}
                        >
                            <Heading
                                fontFamily="Roboto"
                                fontWeight="300"
                                fontSize="40px"
                                leading="45px"
                                textAlign="center"
                                marginX="12px"
                                dangerouslySetInnerHTML={{
                                    __html: newsLetterData?.heading
                                }}
                            />

                            <Text
                                fontWeight="300"
                                fontFamily="Roboto"
                                lineHeight="18.75px"
                                fontSize="14px"
                                align="center"
                            >
                                {newsLetterData?.body}
                            </Text>
                            <Flex
                                flexDirection="column"
                                gap="12px"
                                marginX="12px"
                            >
                                {form.formState.errors?.global && (
                                    <Alert status="error">
                                        <AlertIcon
                                            color="red.500"
                                            boxSize={4}
                                        />
                                        <Text fontSize="sm" ml={3}>
                                            {
                                                form.formState.errors.global
                                                    .message
                                            }
                                        </Text>
                                    </Alert>
                                )}
                                <Box>
                                    <Input
                                        type="email"
                                        {...form.register(
                                            inputNameFromCmsName(
                                                emailInput?.name
                                            )
                                        )}
                                        borderRadius="20px"
                                        px="13px"
                                        pt="6px"
                                        pb="5px"
                                        placeholder={formatMessage({
                                            defaultMessage: 'Email',
                                            id: 'email_input'
                                        })}
                                        height="22px"
                                        fontFamily="Roboto"
                                        fontSize="10px"
                                        _placeholder={{
                                            display: 'flex',
                                            height: '22px',
                                            alignItems: 'center'
                                        }}
                                        border="none"
                                        bg="white"
                                    />
                                    {form.formState?.errors.email && (
                                        <Text
                                            fontSize="14px"
                                            color="red.500"
                                            marginTop="6px"
                                        >
                                            {emailInput.errorMessage}
                                        </Text>
                                    )}
                                </Box>

                                <Link
                                    buttonStyles={styles.submitButton}
                                    type="primary"
                                    isExternal
                                    openLinkInNewTab={true}
                                    buttonType="submit"
                                    isLoading={newsletterMutation.isLoading}
                                    // NEED TO CHANGE THE TYPE PROP
                                    // type="submit"
                                >
                                    {newsLetterData?.content?.submit?.text}
                                </Link>
                            </Flex>
                            <LegalLink />
                        </Flex>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

NewsLetterModal.displayName = 'NewsLetterModal'

NewsLetterModal.propTypes = {
    data: PropTypes.shape({
        heading: PropTypes.string,
        headingType: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
        body: PropTypes.string,
        content: PropTypes.shape({
            name: PropTypes.string,
            successMessage: PropTypes.string,
            errorMessage: PropTypes.string,
            afterSuccessLink: PropTypes.shape({
                url: PropTypes.string,
                text: PropTypes.string,
                type: PropTypes.string,
                iconName: PropTypes.string
            }),
            submit: PropTypes.shape({
                url: PropTypes.string,
                text: PropTypes.string,
                iconName: PropTypes.string,
                type: PropTypes.string
            }),
            fieldsCollection: PropTypes.shape({
                items: PropTypes.arrayOf(
                    PropTypes.shape({
                        label: PropTypes.string,
                        name: PropTypes.string,
                        type: PropTypes.string,
                        required: PropTypes.bool,
                        placeholder: PropTypes.string,
                        errorMessage: PropTypes.string
                    })
                )
            })
        }),
        media: PropTypes.shape({
            desktop: PropTypes.shape({
                url: PropTypes.string
            })
        }),
        mediaAlignment: PropTypes.oneOf(['left', 'right'])
    }),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
}

export default NewsLetterModal
